import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import Currency from 'react-currency-formatter';

const Overlay = styled.div`${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}`;
const Modal = styled.div`${tw`bg-white rounded-lg p-8 w-full max-w-2xl max-h-screen overflow-y-auto`}`;
const Title = tw.h2`text-xl font-bold mb-4 text-gray-800`;
const Label = tw.label`block text-sm font-semibold text-gray-600 mb-1`;
const Input = tw.input`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Select = tw.select`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Row = tw.div`flex gap-3 flex-wrap`;
const Col = styled.div`
  flex: 1;
  min-width: 200px;
`;
const ButtonRow = tw.div`flex justify-end gap-3 mt-4`;
const SaveBtn = tw.button`bg-green-500 text-white px-5 py-2 rounded font-semibold hover:bg-green-600 transition`;
const CancelBtn = tw.button`bg-gray-200 text-gray-700 px-5 py-2 rounded font-semibold hover:bg-gray-300 transition`;

const Card = tw.div`border border-gray-200 rounded p-4 mb-4 bg-gray-100`;
const ItemsList = tw.div`divide-y divide-gray-100 max-h-screen overflow-y-auto mb-4 border-b border-t border-gray-100`;
const ItemRow = tw.div`flex items-center justify-between py-2 text-sm`;
const ItemInfo = tw.div`flex items-center gap-3`;
const ThumbImg = styled.img`${tw`w-12 h-12 object-cover rounded`}`;
const ItemPriceInfo = tw.div`text-right`;

export default function AdminOrderForm({ item, onSave, onCancel }) {
    const [status, setStatus] = useState('pending');
    const [deliveryStatus, setDeliveryStatus] = useState('pending');
    const [driver, setDriver] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (item) {
            setStatus(item.status || 'pending');
            setDeliveryStatus(item.deliveryStatus || (item.status === 'delivered' ? 'delivered' : 'pending'));
            setDriver(item.driver || '');
            setDeliveryAddress(item.deliveryAddress || item.customerAddress || '');
            setExpectedDeliveryDate(item.expectedDeliveryDate || '');
        }
    }, [item]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const data = {
                status,
                deliveryStatus,
                driver,
                deliveryAddress,
                expectedDeliveryDate
            };
            await onSave(data);
        } catch (err) {
            console.error('AdminOrderForm save error:', err);
            alert('Save failed: ' + (err?.message || JSON.stringify(err)));
        } finally {
            setSaving(false);
        }
    };

    if (!item) return null;

    return (
        <Overlay onClick={onCancel}>
            <Modal onClick={e => e.stopPropagation()}>
                <Title>Manage Order Detail</Title>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                    Reference: <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#374151' }}>{item.reference}</span>
                </div>

                {/* Items Ordered section */}
                <Card>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.5rem', color: '#374151' }}>Ordered Items</div>
                    <ItemsList>
                        {Array.isArray(item.items) && item.items.length > 0 ? (
                            item.items.map((prod, idx) => (
                                <ItemRow key={idx}>
                                    <ItemInfo>
                                        {prod.imageSrc ? (
                                            <ThumbImg src={prod.imageSrc} alt={prod.title} onError={e => { e.target.style.display = 'none'; }} />
                                        ) : (
                                            <div style={{ width: 48, height: 48, backgroundColor: '#e5e7eb', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '0.65rem' }}>No image</div>
                                        )}
                                        <div>
                                            <div style={{ fontWeight: 600, color: '#374151' }}>{prod.title?.replace(/_/g, ' ')}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Qty: {prod.quantity}</div>
                                            {Array.isArray(prod.basket) && prod.basket.length > 0 && (
                                                <div style={{ fontSize: '0.7rem', color: '#10b981', marginTop: 2 }}>
                                                    {prod.basket.map((b, bidx) => (
                                                        <div key={bidx}>
                                                            • {b.quantity} {b.measure?.replace(/_/g, ' ')} ({b.type?.replace(/_/g, ' ')})
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </ItemInfo>
                                    <ItemPriceInfo>
                                        <div style={{ fontWeight: 600, color: '#374151' }}>₦{prod.total || prod.price * prod.quantity || 0}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>₦{prod.price} each</div>
                                    </ItemPriceInfo>
                                </ItemRow>
                            ))
                        ) : (
                            <div style={{ padding: '1rem', textAlign: 'center', fontStyle: 'italic', color: '#9ca3af' }}>No products listed in order records</div>
                        )}
                    </ItemsList>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#4b5563', padding: '0.25rem 0' }}>
                        <span>Subtotal:</span>
                        <span>₦{item.subtotal?.toFixed(2) || (item.total - item.tax)?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#4b5563', padding: '0.25rem 0' }}>
                        <span>VAT (7.5%):</span>
                        <span>₦{item.tax?.toFixed(2) || (item.total * 0.075)?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 700, color: '#10b981', paddingTop: '0.5rem', borderTop: '1px dashed #d1d5db' }}>
                        <span>Total:</span>
                        <span>₦{item.total?.toFixed(2) || '0.00'}</span>
                    </div>
                </Card>

                {/* Customer Info section */}
                <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.75rem', color: '#374151' }}>Customer Contact Info</div>
                <Row style={{ marginBottom: '1rem' }}>
                    <Col>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Customer Name</div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{item.userName || 'N/A'}</div>
                    </Col>
                    <Col>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Customer Email</div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{item.userEmail || 'N/A'}</div>
                    </Col>
                    <Col>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Customer Phone</div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{item.userPhone || 'N/A'}</div>
                    </Col>
                </Row>
                <Row style={{ marginBottom: '1.5rem' }}>
                    <Col style={{ flex: '1 1 100%' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Customer Profile Address</div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1f2937' }}>
                            {item.customerAddress || 'No address set in profile'}
                        </div>
                    </Col>
                </Row>

                {/* Update controls form */}
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col>
                            <Label>Order / Payment Status</Label>
                            <Select value={status} onChange={e => setStatus(e.target.value)}>
                                <option value="pending">Pending Payment</option>
                                <option value="success">Success / Paid</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                                <option value="cancelled">Cancelled</option>
                            </Select>
                        </Col>
                        <Col>
                            <Label>Delivery Status</Label>
                            <Select value={deliveryStatus} onChange={e => setDeliveryStatus(e.target.value)}>
                                <option value="pending">Pending Dispatch</option>
                                <option value="dispatching">Dispatching</option>
                                <option value="in_transit">In Transit / Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="failed">Delivery Failed</option>
                            </Select>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Label>Assigned Delivery Driver</Label>
                            <Input value={driver} onChange={e => setDriver(e.target.value)} placeholder="e.g. Samuel Ade" />
                        </Col>
                        <Col>
                            <Label>Expected Delivery Date</Label>
                            <Input type="date" value={expectedDeliveryDate} onChange={e => setExpectedDeliveryDate(e.target.value)} />
                        </Col>
                    </Row>

                    <Label>Delivery Address</Label>
                    <Input value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} placeholder="e.g. 12, Isaac John Street, Ikeja, Lagos" />

                    <ButtonRow>
                        <CancelBtn type="button" onClick={onCancel} disabled={saving}>Cancel</CancelBtn>
                        <SaveBtn type="submit" disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </SaveBtn>
                    </ButtonRow>
                </form>
            </Modal>
        </Overlay>
    );
}
